import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { SharedPrizeCarousel } from 'components/prize-carousel/SharedPrizeCarousel';
import {
  BOOST_PAGE_PRIZES,
  homepagePrizeCarouselItems,
} from 'constants/Prizes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { DaymadeDesignSystemPreviewComponent } from '../';

const ComponentPreview = observer(() => {
  const [desktopWidth, setDesktopWidth] = useState(50);
  const [mobileWidth, setMobileWidth] = useState(60);
  const [overlayText, setOverlayText] = useState(true);
  const [overlayVariant, setOverlayVariant] = useState<'gradient' | 'solid'>(
    'solid',
  );
  const [showButtonsOnMobile, setShowButtonsOnMobile] = useState(true);
  const [compactDesktopTypography, setCompactDesktopTypography] =
    useState(true);

  const titleClassName = compactDesktopTypography
    ? 'aboutUsTypographyBody'
    : undefined;
  const subheadingClassName = compactDesktopTypography
    ? 'aboutUsTypographyBodySmall'
    : undefined;

  return (
    <AboutUsModule background="white">
      <AboutUsSiteContainer>
        <h3 className="aboutUsTypographyHeaderLarge">Shared Prize Carousel</h3>
        <p>
          Shared carousel base component with page-specific presets. The blocks
          below mirror how it appears on Home and Enter Flow pages.
        </p>

        <div style={{ marginTop: 40 }}>
          <h4 className="aboutUsTypographyHeaderMedium">
            Customizable shared carousel controls
          </h4>
          <p>
            Playground for testing the shared component settings independent of
            page-specific presets.
          </p>

          <div style={{ marginBottom: 24 }}>
            <p className="noMargin" style={{ marginBottom: 8 }}>
              <strong>Controls</strong>
            </p>

            <p className="noMargin" style={{ marginBottom: 12 }}>
              <label htmlFor="shared-carousel-overlay-variant">
                Image overlay:
              </label>{' '}
              <select
                id="shared-carousel-overlay-variant"
                value={overlayVariant}
                onChange={(e) =>
                  setOverlayVariant(
                    e.currentTarget.value as 'gradient' | 'solid',
                  )
                }
              >
                <option value="solid">Solid</option>
                <option value="gradient">Gradient</option>
              </select>
            </p>

            <p className="noMargin" style={{ marginBottom: 12 }}>
              <label htmlFor="shared-carousel-text-position">
                Text position:
              </label>{' '}
              <select
                id="shared-carousel-text-position"
                value={overlayText ? 'overlayBottom' : 'default'}
                onChange={(e) =>
                  setOverlayText(e.currentTarget.value === 'overlayBottom')
                }
              >
                <option value="overlayBottom">Overlay bottom</option>
                <option value="default">Default (below image)</option>
              </select>
            </p>

            <p className="noMargin" style={{ marginBottom: 12 }}>
              <label htmlFor="shared-carousel-desktop-width">
                Desktop card width: {desktopWidth}%
              </label>
              <br />
              <input
                id="shared-carousel-desktop-width"
                type="range"
                min={30}
                max={70}
                step={1}
                value={desktopWidth}
                onChange={(e) => setDesktopWidth(Number(e.currentTarget.value))}
              />
            </p>

            <p className="noMargin" style={{ marginBottom: 12 }}>
              <label htmlFor="shared-carousel-mobile-width">
                Mobile card width: {mobileWidth}%
              </label>
              <br />
              <input
                id="shared-carousel-mobile-width"
                type="range"
                min={45}
                max={90}
                step={1}
                value={mobileWidth}
                onChange={(e) => setMobileWidth(Number(e.currentTarget.value))}
              />
            </p>

            <p className="noMargin" style={{ marginBottom: 12 }}>
              <input
                id="shared-carousel-mobile-buttons"
                type="checkbox"
                checked={showButtonsOnMobile}
                onChange={() => setShowButtonsOnMobile((value) => !value)}
              />{' '}
              <label htmlFor="shared-carousel-mobile-buttons">
                Show carousel buttons on mobile
              </label>
            </p>

            <p className="noMargin">
              <input
                id="shared-carousel-compact-typography"
                type="checkbox"
                checked={compactDesktopTypography}
                onChange={() => setCompactDesktopTypography((value) => !value)}
              />{' '}
              <label htmlFor="shared-carousel-compact-typography">
                Use compact typography overrides
              </label>
            </p>
          </div>

          <SharedPrizeCarousel
            items={BOOST_PAGE_PRIZES}
            mobileCardWidth={`${mobileWidth}%`}
            desktopCardWidth={`${desktopWidth}%`}
            desktopInlinePadding="var(--dm2025-sitePadding)"
            slidePadding="4px"
            textPosition={overlayText ? 'overlayBottom' : 'default'}
            overlayVariant={overlayVariant}
            titleClassName={titleClassName}
            subheadingClassName={subheadingClassName}
            showButtonsOnMobile={showButtonsOnMobile}
            buttonHorizontalPosition="-10px"
            linkBuilder={(item) => item.slug}
          />
        </div>

        <div style={{ marginTop: 40, marginBottom: 40 }}>
          <h4 className="aboutUsTypographyHeaderMedium">Home page version</h4>
          <p className="aboutUsTypographyBodySmall">
            Route: <strong>/</strong> (prize section)
          </p>
          <SharedPrizeCarousel
            items={homepagePrizeCarouselItems}
            textPosition="default"
            overlayVariant="gradient"
            showButtonsOnMobile={false}
            showButtonsOnDesktop={true}
            desktopInlinePadding="40px"
            slidePadding="var(--dm2025-gap-8)"
            linkBuilder={(item) =>
              item.slug ? `/prizes/details/${item.slug}` : null
            }
          />
        </div>

        <div>
          <h4 className="aboutUsTypographyHeaderMedium">Enter flow version</h4>
          <p className="aboutUsTypographyBodySmall">
            Route: <strong>/enternow/4</strong> (boost step)
          </p>
          <SharedPrizeCarousel
            items={BOOST_PAGE_PRIZES}
            mobileCardWidth="60%"
            desktopCardWidth="50%"
            desktopInlinePadding="var(--dm2025-sitePadding)"
            slidePadding="4px"
            textPosition="overlayBottom"
            overlayVariant="solid"
            titleClassName="aboutUsTypographyBody"
            subheadingClassName="aboutUsTypographyBodySmall"
            buttonHorizontalPosition="-10px"
            linkBuilder={(item) => item.slug}
          />
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
}) as DaymadeDesignSystemPreviewComponent;

ComponentPreview.daymadeDesignSystemInfo = {
  name: 'Shared Prize Carousel',
};

export default ComponentPreview;
